import { AsyncPipe, NgClass } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  NgZone,
  Renderer2,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router, RouterLink } from "@angular/router";
import { TranslocoModule } from "@ngneat/transloco";
import type { Nullable, User } from "@models";
import { UserCredentialsPipe } from "@pipes";
import { AuthService, FuturesModeService, LocalStorageService, TranslateService } from "@services";
import { Store, clearCurrentUser, selectCurrentUser, selectUserTokens } from "@store";
import { Observable, withLatestFrom } from "rxjs";
import { DisableLightThemeDirective } from "src/app/core/directive/disable-light-theme.directive";
import { DeviceService } from "src/app/core/service/device.service";
import { ScrollBlockService } from "src/app/core/service/scroll-block.service";
import { CustomDialogComponent } from "../custom-dialog/custom-dialog.component";
import { BreadcrumbComponent } from "../breadcrumb/breadcrumb.component";
import { ButtonComponent } from "../button/button.component";
import { CommonService } from "../common.service";
import { LanguageDropdownComponent } from "../language-dropdown/language-dropdown.component";
import { SvgIconComponent } from "../social-icons/svg-icon/svg-icon.component";
import { ThemeSwitcherComponent } from "../theme-switcher/theme-switcher.component";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    BreadcrumbComponent,
    RouterLink,
    AsyncPipe,
    UserCredentialsPipe,
    NgClass,
    TranslocoModule,
    LanguageDropdownComponent,
    ThemeSwitcherComponent,
    DisableLightThemeDirective,
    ButtonComponent,
    SvgIconComponent,
    CustomDialogComponent,
  ],
})
export class HeaderComponent {
  public showLogoutDialog = false;
  public user$: Observable<Nullable<User>> = this._store.select(selectCurrentUser);
  public tokensAmount$: Observable<number> = this._store.select(selectUserTokens);
  public isCollapsed: boolean = false;
  public menuVisible: boolean = false;
  public totalToken: number;
  public isMobile$: Observable<boolean> = this._deviceService.isBelow(991);

  constructor(
    private _router: Router,
    private _commonService: CommonService,
    private _elementRef: ElementRef,
    private _renderer: Renderer2,
    private _store: Store,
    private _ngZone: NgZone,
    private _translateService: TranslateService,
    private _localStorageService: LocalStorageService,
    private _authService: AuthService,
    private _scrollBlockService: ScrollBlockService,
    private _deviceService: DeviceService,
    private _destroyRef: DestroyRef,
    private _futuresModeService: FuturesModeService,
  ) {
    this._commonService.sidebar
      .pipe(withLatestFrom(this.isMobile$), takeUntilDestroyed(this._destroyRef))
      .subscribe(([, isMobile]) => {
        this.isCollapsed = !this.isCollapsed;

        if (this.isCollapsed && isMobile) {
          this._scrollBlockService.blockScroll();
        } else {
          this._scrollBlockService.unblockScroll();
        }
      });
  }

  @HostListener("window:scroll", [])
  public onWindowScroll(): void {
    this._ngZone.runOutsideAngular(() => {
      const header = document.getElementById("kt_header");

      if (header) {
        const scrollTop =
          window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
        if (scrollTop > 0) {
          this._renderer.addClass(header, "Header_header_scroll");
        } else {
          this._renderer.removeClass(header, "Header_header_scroll");
        }
      }
    });
  }

  public logout(): void {
    this._authService.logout();
    const selectedLanguage = this._translateService.getSelectedLanguage();
    this._localStorageService.clear();
    this._translateService.setLanguage(selectedLanguage);
    this._router.navigate(["/auth/login"]);
    this._store.dispatch(clearCurrentUser());
    this.showLogoutDialog = false;
  }

  public closeDialog(): void {
    this.showLogoutDialog = false;
  }

  public toggleSidebar(): void {
    this._commonService.showSidenav();
  }

  public prompt(): void {
    this.showLogoutDialog = true;
  }

  public expandMenu(): void {
    this.menuVisible = !this.menuVisible;
    if (this.menuVisible) {
      this._addOutsideClickListener();
    } else {
      this._removeOutsideClickListener();
    }
  }

  private _addOutsideClickListener(): void {
    this._ngZone.runOutsideAngular(() => {
      document.addEventListener("click", this._handleOutsideClick);
    });
  }

  private _removeOutsideClickListener(): void {
    this._ngZone.runOutsideAngular(() => {
      document.removeEventListener("click", this._handleOutsideClick);
    });
  }

  private readonly _handleOutsideClick = (event: Event): void => {
    if (!this._elementRef.nativeElement.contains(event.target)) {
      this.menuVisible = false;
      this._removeOutsideClickListener();
    }
  };

  public navigateToDashboard(): void {
    const url: string = this._futuresModeService.isFuturesRoute()
      ? "futures/dashboard"
      : "dashboard";
    this._router.navigate([url]);
  }
}
